import React, { useEffect, useState } from 'react';

import { Center, Heading, useMediaQuery } from '@chakra-ui/react';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LoginParams } from '@/types/query-params';

import { SummaryPage } from '@/routes/private';

import { LoginForm } from '@/components/Login/form';
import { FormContainer } from '@/components/form/form-container';

import { AppRoutesEnum } from '@/enum/appRoutes.enum';
import { MediaQueryAmountsEnum } from '@/enum/mediaQueryAmounts.enum';
import { adminPermissions } from '@/enum/permission.enum';

import { useAuth } from '@/context/auth.context';

import { ToastTypes, useCustomToast } from '@/hooks/useCustomToast';
import useUserAccess from '@/hooks/useUserAccess';

import { getErrorMessageByCode } from '@/utils/getErrorMessage';

const LoginPage: React.FC<{ shouldRedirect?: boolean }> = ({
  shouldRedirect = true,
}) => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery(MediaQueryAmountsEnum.isMobile);
  const customToast = useCustomToast();
  const navigate = useNavigate();
  const [loginCalled, setLoginCalled] = useState(false);
  const { hasAccess: hasAnyAdminPermission } = useUserAccess(adminPermissions);

  const { login, isLoggingIn, isLoading } = useAuth();

  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      if (loginCalled) {
        customToast(t('login.successMessage'), ToastTypes.success);
      }

      if (shouldRedirect) {
        navigate(
          hasAnyAdminPermission
            ? AppRoutesEnum.adminSummary
            : AppRoutesEnum.summary,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    SummaryPage.preload();
  }, []);

  const onSubmit: SubmitHandler<FieldValues> = async (userData) => {
    if (window.lintrk) {
      window.lintrk('track', { conversion_id: 9467818 });
    }

    try {
      const loginData: Omit<LoginParams, 'security'> = {
        email: userData.emailAddress,
        password: userData.password,
      };

      if (userData.code) {
        loginData.code = userData.code;
      }

      setLoginCalled(true);
      await login(loginData);
    } catch (e) {
      customToast(t(getErrorMessageByCode(e.data.code)), ToastTypes.error);
    }
  };

  return (
    <Center h='100%' w='100%'>
      <FormContainer mt='0px'>
        <Heading as='h1' color='app.pink' pb='lg' size={isMobile ? 'md' : 'lg'}>
          {t('login.title')}
        </Heading>

        <LoginForm isLoading={isLoggingIn || isLoading} onSubmit={onSubmit} />
      </FormContainer>
    </Center>
  );
};

export default LoginPage;
